import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Sparkles, Loader2, Check, AlertCircle } from "lucide-react";

export default function CheckoutSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState({ status: "polling", attempt: 0 });
    const stop = useRef(false);

    useEffect(() => {
        stop.current = false;
        const max = 30;

        const poll = async (n = 0) => {
            if (stop.current) return;
            if (!sessionId) {
                setState({ status: "error" });
                return;
            }
            if (n >= max) {
                setState({ status: "timeout" });
                return;
            }
            try {
                const { data } = await api.get(`/checkout/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setState({ status: "paid" });
                    return;
                }
                if (data.status === "expired") {
                    setState({ status: "expired" });
                    return;
                }
                setState({ status: "polling", attempt: n + 1 });
                setTimeout(() => poll(n + 1), 2000);
            } catch (err) {
                console.warn("checkout poll error, retrying:", err?.message || err);
                setTimeout(() => poll(n + 1), 2000);
            }
        };
        poll(0);

        return () => { stop.current = true; };
    }, [sessionId]);

    return (
        <div className="min-h-[80vh] flex items-center justify-center px-6">
            <div className="max-w-lg w-full rounded-3xl border border-white/10 bg-surface p-10 text-center">
                {state.status === "polling" && (
                    <>
                        <Loader2 className="w-10 h-10 animate-spin text-rose mx-auto" />
                        <h1 className="font-serif text-3xl mt-6">Finalizing your subscription…</h1>
                        <p className="text-white/60 mt-2">Hang tight — confirming with Stripe.</p>
                    </>
                )}
                {state.status === "paid" && (
                    <>
                        <div className="w-14 h-14 rounded-full bg-rose/15 mx-auto flex items-center justify-center">
                            <Check className="w-7 h-7 text-rose" />
                        </div>
                        <p className="text-xs tracking-[0.3em] uppercase text-rose mt-6">Welcome aboard</p>
                        <h1 className="font-serif text-4xl mt-2">You're glowing ✦</h1>
                        <p className="text-white/65 mt-3">Your subscription is now active for the next 30 days. Book your first appointment now.</p>
                        <Link to="/portal" data-testid="success-portal-btn" className="inline-block mt-8 px-7 py-3 rounded-full bg-rose text-ink hover:bg-roseHover transition font-medium">
                            Go to my portal <Sparkles className="inline w-4 h-4 ml-1" />
                        </Link>
                    </>
                )}
                {(state.status === "error" || state.status === "expired" || state.status === "timeout") && (
                    <>
                        <AlertCircle className="w-10 h-10 text-rose mx-auto" />
                        <h1 className="font-serif text-3xl mt-6">Something went sideways</h1>
                        <p className="text-white/60 mt-2">
                            {state.status === "expired"
                                ? "Your checkout session expired."
                                : state.status === "timeout"
                                ? "Your payment is still processing — it may take a couple of minutes. Check your portal shortly; if it doesn't activate, message us and we'll sort it instantly."
                                : "We couldn't confirm your payment. If you were charged, contact us in the messages tab."}
                        </p>
                        <Link to="/portal/subscription" data-testid="success-retry-btn" className="inline-block mt-8 px-7 py-3 rounded-full border border-rose text-rose hover:bg-rose hover:text-ink transition font-medium">
                            Back to subscription
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
}
